import type { HomePageFourPillars } from "../services/HomeService";
import { Activity, MapPin, Scale, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type PillarKey =
  | "glucose_score"
  | "microbiome_score"
  | "pas_score"
  | "wms_score";

export interface PillarConfig {
  key: PillarKey;
  label: string;
  weightPercent: number;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  badgeBg: string;
  badgeText: string;
  barClass: string;
  subtitle: string;
  description: string;
  bullets?: { title: string; text: string }[];
}

export const FOUR_PILLARS: PillarConfig[] = [
  {
    key: "glucose_score",
    label: "Glucose",
    weightPercent: 35,
    icon: Zap,
    iconBg: "bg-[#737DFE]/10 dark:bg-[#737DFE]/15",
    iconColor: "text-[#737DFE]",
    badgeBg: "bg-[#737DFE]/10 dark:bg-[#737DFE]/15",
    badgeText: "text-[#737DFE]",
    barClass: "bg-[#737DFE]",
    subtitle: "How steady your blood sugar stays",
    description:
      "Built from the quality of today's meals and their predicted glucose spikes. Better meal scores and smaller spikes lift this pillar. It carries the most weight, because blood-sugar stability is the foundation of metabolic health.",
  },
  {
    key: "microbiome_score",
    label: "Microbiome",
    weightPercent: 15,
    icon: MapPin,
    iconBg: "bg-[#737DFE]/10 dark:bg-[#737DFE]/15",
    iconColor: "text-[#6268e8]",
    badgeBg: "bg-[#737DFE]/10 dark:bg-[#737DFE]/15",
    badgeText: "text-[#6268e8]",
    barClass: "bg-[#6268e8]",
    subtitle: "How well you're feeding your gut · last 7 days",
    description:
      "Looks at the past week of eating to gauge gut health. Diverse, fibre-rich food builds a healthier microbiome:",
    bullets: [
      { title: "Plant variety", text: "aim for 30 different plants a week" },
      { title: "Fermented foods", text: "in about half your meals" },
      { title: "Daily fibre", text: "around 25g a day" },
      { title: "Prebiotic foods", text: "in most of your meals" },
      { title: "Less ultra-processed", text: "fewer packaged, processed items" },
    ],
  },
  {
    key: "pas_score",
    label: "Physical",
    weightPercent: 25,
    icon: Activity,
    iconBg: "bg-[#737DFE]/10 dark:bg-[#737DFE]/15",
    iconColor: "text-[#737DFE]",
    badgeBg: "bg-[#737DFE]/10 dark:bg-[#737DFE]/15",
    badgeText: "text-[#737DFE]",
    barClass: "bg-[#737DFE]",
    subtitle: "How much your movement helps glucose",
    description:
      "Earned from your daily activity — especially movement after meals. Walking and exercise help your body clear glucose more efficiently, so more movement raises this pillar.",
  },
  {
    key: "wms_score",
    label: "Weight",
    weightPercent: 25,
    icon: Scale,
    iconBg: "bg-[#737DFE]/10 dark:bg-[#737DFE]/15",
    iconColor: "text-[#5258d4]",
    badgeBg: "bg-[#737DFE]/10 dark:bg-[#737DFE]/15",
    badgeText: "text-[#5258d4]",
    barClass: "bg-[#5258d4]",
    subtitle: "How your body composition is tracking",
    description:
      "Reflects your weight-management progress. Body composition shapes insulin sensitivity, which influences how your body responds to every meal you eat.",
  },
];

export const PILLAR_WEIGHT_SEGMENTS = [
  { label: "Glucose", percent: 35, color: "bg-[#737DFE]" },
  { label: "Physical", percent: 25, color: "bg-[#737DFE]/85" },
  { label: "Weight", percent: 25, color: "bg-[#6268e8]" },
  { label: "Microbiome", percent: 15, color: "bg-[#5258d4]" },
] as const;

export function getLowestPillarKey(
  pillars: HomePageFourPillars,
): PillarKey | null {
  let lowestKey: PillarKey | null = null;
  let lowestValue = Number.POSITIVE_INFINITY;

  for (const pillar of FOUR_PILLARS) {
    const value = pillars[pillar.key];
    if (typeof value === "number" && value < lowestValue) {
      lowestValue = value;
      lowestKey = pillar.key;
    }
  }

  return lowestKey;
}
